import { getDefaultMk8sInput } from "./utils";
import { Mk8sCluster } from "../../../../schema/types/mk8s/entity/mk8s";
import { Mk8sInput } from "../../../../schema/types/mk8s/input/mk8s";
import jsYaml from "js-yaml";
import { v4 as uuidv4 } from "uuid";
import { tagsToInput } from "../../../../mst/kinds/utils";
import { AwsPool, NetworkingConfig } from "../../../../schema/types/mk8s/entity/aws";
import {
  AutoscalerConfigInput,
  NetworkingConfigInput,
  TaintInput,
  UnmanagedPoolInput,
} from "../../../../schema/types/mk8s/input/common";
import { AutoscalerConfig, Taint, UnmanagedPool } from "../../../../schema/types/mk8s/entity/common";
import { AwsPoolInput } from "../../../../schema/types/mk8s/input/aws";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { AzurePool } from "../../../../schema/types/mk8s/entity/azure";
import { AzurePoolInput } from "../../../../schema/types/mk8s/input/azure";
import { DigitalOceanPool } from "../../../../schema/types/mk8s/entity/digitalocean";
import { DigitalOceanPoolInput } from "../../../../schema/types/mk8s/input/digitalocean";
import { EphemeralPool } from "../../../../schema/types/mk8s/entity/ephemeral";
import { EphemeralPoolInput } from "../../../../schema/types/mk8s/input/ephemeral";
import { HetznerPool } from "../../../../schema/types/mk8s/entity/hetzner";
import { HetznerPoolInput } from "../../../../schema/types/mk8s/input/hetzner";
import { LambdalabsPool } from "../../../../schema/types/mk8s/entity/lambdalabs";
import { LambdalabsPoolInput } from "../../../../schema/types/mk8s/input/lambdalabs";
import { LinodePool } from "../../../../schema/types/mk8s/entity/linode";
import { LinodePoolInput } from "../../../../schema/types/mk8s/input/linode";
import { OblivusPool } from "../../../../schema/types/mk8s/entity/oblivus";
import { OblivusPoolInput } from "../../../../schema/types/mk8s/input/oblivus";
import { PaperspacePool } from "../../../../schema/types/mk8s/entity/paperspace";
import { PaperspacePoolInput } from "../../../../schema/types/mk8s/input/paperspace";
import { TritonPool } from "../../../../schema/types/mk8s/entity/triton";
import { TritonPoolInput } from "../../../../schema/types/mk8s/input/triton";
import { GcpPool } from "../../../../schema/types/mk8s/entity/gcp";
import { GcpPoolInput } from "../../../../schema/types/mk8s/input/gcp";
import { linksOf } from "../../../../services/cpln";

export function autoscalerToInput(a?: AutoscalerConfig): AutoscalerConfigInput {
  if (!a) {
    return {
      expander: [],
      unneededTime: "",
      unreadyTime: "",
      utilizationThreshold: "",
    };
  }

  return {
    expander: a.expander || [],
    unneededTime: a.unneededTime,
    unreadyTime: a.unreadyTime,
    utilizationThreshold: a.utilizationThreshold === undefined ? "" : String(a.utilizationThreshold),
  };
}

export function networkingToInput(n?: NetworkingConfig): NetworkingConfigInput {
  if (!n) {
    return {
      serviceNetwork: "",
      podNetwork: "",
      dnsForwarder: "",
    };
  }

  return {
    serviceNetwork: n.serviceNetwork || "",
    podNetwork: n.podNetwork || "",
    dnsForwarder: n.dnsForwarder || "",
  };
}

export function taintsToInput(t?: Taint[]): TaintInput[] {
  const res: TaintInput[] = [];
  if (!t) {
    return res;
  }
  for (const taint of t) {
    res.push({ id: uuidv4(), key: taint.key || "", value: taint.value || "", effect: taint.effect || "NoExecute" });
  }
  return res;
}

export function awsNodePoolToInput(np: AwsPool): AwsPoolInput {
  const i: AwsPoolInput = {
    id: uuidv4(),
    name: np.name,
    bootDiskSize: np.bootDiskSize === undefined ? "" : String(np.bootDiskSize),
    extraSecurityGroupIds: (np.extraSecurityGroupIds || []).map((esg) => ({ id: uuidv4(), value: esg })),
    instanceTypes: (np.instanceTypes || []).map((it) => ({ id: uuidv4(), value: it })),
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    onDemandBaseCapacity: np.onDemandBaseCapacity === undefined ? "" : String(np.onDemandBaseCapacity),
    onDemandPercentageAboveBaseCapacity:
      np.onDemandPercentageAboveBaseCapacity === undefined ? "" : String(np.onDemandPercentageAboveBaseCapacity),
    overrideImage: {
      type: np.overrideImage?.exact ? "exact" : np.overrideImage?.recommended ? "recommended" : "none",
      exact: np.overrideImage?.exact || "",
      recommended: np.overrideImage?.recommended || "",
    },
    spotAllocationStrategy: np.spotAllocationStrategy || "",
    subnetIds: (np.subnetIds || []).map((si) => ({ id: uuidv4(), value: si })),
    cpuOptions: {
      nestedVirtualization: np.cpuOptions?.nestedVirtualization || false,
    },
  };
  return i;
}

export function azureNodePoolToInput(np: AzurePool): AzurePoolInput {
  const i: AzurePoolInput = {
    id: uuidv4(),
    name: np.name,
    bootDiskSize: np.bootDiskSize === undefined ? "" : String(np.bootDiskSize),
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    overrideImage: {
      type: np.overrideImage?.recommended ? "recommended" : np.overrideImage?.reference ? "reference" : "none",
      recommended: np.overrideImage?.recommended || "",
      reference: {
        offer: np.overrideImage?.reference?.offer || "",
        publisher: np.overrideImage?.reference?.publisher || "",
        sku: np.overrideImage?.reference?.sku || "",
        version: np.overrideImage?.reference?.version || "",
      },
    },
    size: np.size,
    subnetId: np.subnetId,
    zones: (np.zones || []).map((z) => String(z)),
  };
  return i;
}

export function digitaloceanNodePoolToInput(np: DigitalOceanPool): DigitalOceanPoolInput {
  const i: DigitalOceanPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    overrideImage: np.overrideImage || "",
    dropletSize: np.dropletSize,
  };
  return i;
}

export function ephemeralNodePoolToInput(np: EphemeralPool): EphemeralPoolInput {
  const i: EphemeralPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    arch: np.arch,
    count: np.count === undefined ? "" : String(np.count),
    cpu: np.cpu,
    flavor: np.flavor,
    memory: np.memory,
  };
  return i;
}

export function gcpNodePoolToInput(np: GcpPool): GcpPoolInput {
  const i: GcpPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    machineType: np.machineType,
    assignPublicIP: np.assignPublicIP || false,
    preemptible: np.preemptible || false,
    zone: np.zone || "",
    overrideImage: {
      type: np.overrideImage?.exact
        ? "exact"
        : np.overrideImage?.recommended
        ? "recommended"
        : np.overrideImage?.family
        ? "family"
        : "none",
      exact: np.overrideImage?.exact || "",
      recommended: np.overrideImage?.recommended || "",
      family: {
        project: np.overrideImage?.family?.project || "",
        family: np.overrideImage?.family?.family || "",
      },
    },
    bootDiskSize: np.bootDiskSize === undefined ? "" : String(np.bootDiskSize),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    subnet: np.subnet,
    localPersistentDisks: np.localPersistentDisks === undefined ? "" : String(np.localPersistentDisks),
  };
  return i;
}

export function hetznerNodePoolToInput(np: HetznerPool): HetznerPoolInput {
  const i: HetznerPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    overrideImage: np.overrideImage || "",
    serverType: np.serverType,
  };
  return i;
}

export function lambdalabsNodePoolToInput(np: LambdalabsPool): LambdalabsPoolInput {
  const i: LambdalabsPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    instanceType: np.instanceType,
  };
  return i;
}

export function linodeNodePoolToInput(np: LinodePool): LinodePoolInput {
  const i: LinodePoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    overrideImage: np.overrideImage || "",
    serverType: np.serverType,
    subnetId: np.subnetId,
  };
  return i;
}

export function oblivusNodePoolToInput(np: OblivusPool): OblivusPoolInput {
  const i: OblivusPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    flavor: np.flavor,
  };
  return i;
}

export function paperspaceNodePoolToInput(np: PaperspacePool): PaperspacePoolInput {
  const i: PaperspacePoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    bootDiskSize: np.bootDiskSize === undefined ? "" : String(np.bootDiskSize),
    machineType: np.machineType,
    publicIpType: np.publicIpType,
  };
  return i;
}

export function tritonNodePoolToInput(np: TritonPool): TritonPoolInput {
  const i: TritonPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
    minSize: np.minSize === undefined ? "" : String(np.minSize),
    maxSize: np.maxSize === undefined ? "" : String(np.maxSize),
    overrideImageId: np.overrideImageId || "",
    packageId: np.packageId,
    privateNetworkIds: (np.privateNetworkIds || []).map((pni) => ({ id: uuidv4(), value: pni })),
    tritonTags: tagsToInput(np.tritonTags),
    publicNetworkId: np.publicNetworkId || "",
  };
  return i;
}

export function unmanagedNodePoolToInput(np: UnmanagedPool): UnmanagedPoolInput {
  const i: UnmanagedPoolInput = {
    id: uuidv4(),
    name: np.name,
    labels: tagsToInput(np.labels),
    taints: taintsToInput(np.taints),
  };
  return i;
}

export function mk8sToInput(m?: Mk8sCluster): Mk8sInput {
  if (!m) {
    return getDefaultMk8sInput();
  }

  const i = getDefaultMk8sInput();

  i.name = m.name;
  i.description = m.description || i.name;
  i.version = m.version;
  i.id = m.id;
  i.tags = tagsToInput(m.tags);
  i.meta.isCreate = false;
  i.meta.clusterLink = linksOf(m).self!;

  i.specVersion = m.spec.version;

  // provider
  if (m.spec.provider.aws) {
    const a = m.spec.provider.aws;
    i.provider = "aws";
    i.aws = {
      autoscaler: autoscalerToInput(a.autoscaler),
      networking: networkingToInput(a.networking),
      awsTags: tagsToInput(a.awsTags),
      deployRoleArn: a.deployRoleArn,
      image: {
        type: a.image.exact ? "exact" : "recommended",
        exact: a.image.exact || "",
        recommended: a.image.recommended || "",
      },
      region: a.region,
      vpcId: a.vpcId,
      // optionals
      deployRoleChain: (a.deployRoleChain || []).map((drc) => ({
        id: uuidv4(),
        roleArn: drc.roleArn || "",
        externalId: drc.externalId || "",
        sessionNamePrefix: drc.sessionNamePrefix || "",
      })),
      diskEncryptionKeyArn: a.diskEncryptionKeyArn || "",
      extraNodePolicies: (a.extraNodePolicies || []).map((enp) => ({ id: uuidv4(), value: enp })),
      keyPair: a.keyPair || "",
      nodePools: [],
      preInstallScript: a.preInstallScript || "",
      securityGroupIds: (a.securityGroupIds || []).map((sg) => ({ id: uuidv4(), value: sg })),
      skipCreateRoles: a.skipCreateRoles || false,
    };

    const nodePools = (a.nodePools || []).map((np) => awsNodePoolToInput(np));
    i.aws.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.aws.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.azure) {
    const a = m.spec.provider.azure;
    i.provider = "azure";

    const parsedLink = parseLink(a.sdkSecretLink);

    i.azure = {
      autoscaler: autoscalerToInput(a.autoscaler),
      networking: networkingToInput(a.networking),
      tags: tagsToInput(a.tags),
      resourceGroup: a.resourceGroup,
      image: {
        type: a.image.recommended ? "recommended" : "reference",
        recommended: a.image.recommended || "",
        reference: {
          offer: a.image.reference?.offer || "",
          publisher: a.image.reference?.publisher || "",
          sku: a.image.reference?.sku || "",
          version: a.image.reference?.version || "",
        },
      },
      location: a.location,
      networkId: a.networkId,
      subscriptionId: a.subscriptionId,
      sdkSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      sshKeys: (a.sshKeys || []).map((sk) => ({ id: uuidv4(), value: sk })),
      nodePools: [],
      preInstallScript: a.preInstallScript || "",
    };

    const nodePools = (a.nodePools || []).map((np) => azureNodePoolToInput(np));
    i.azure.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.azure.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.digitalocean) {
    const d = m.spec.provider.digitalocean;
    i.provider = "digitalocean";

    const parsedLink = parseLink(d.tokenSecretLink);

    i.digitalocean = {
      autoscaler: autoscalerToInput(d.autoscaler),
      networking: networkingToInput(d.networking),
      digitalOceanTags: tagsToInput(d.digitalOceanTags),
      image: d.image || "",
      region: d.region,
      vpcId: d.vpcId,
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      sshKeys: (d.sshKeys || []).map((sk) => ({ id: uuidv4(), value: sk })),
      extraSshKeys: (d.extraSshKeys || []).map((sk) => ({ id: uuidv4(), value: sk })),
      reservedIPs: (d.reservedIPs || []).map((sk) => ({ id: uuidv4(), value: sk })),
      nodePools: [],
      preInstallScript: d.preInstallScript || "",
    };

    const nodePools = (d.nodePools || []).map((np) => digitaloceanNodePoolToInput(np));
    i.digitalocean.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.digitalocean.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.ephemeral) {
    const e = m.spec.provider.ephemeral;
    i.provider = "ephemeral";

    i.ephemeral = {
      location: e.location,
      nodePools: [],
    };

    const nodePools = (e.nodePools || []).map((np) => ephemeralNodePoolToInput(np));
    i.ephemeral.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.ephemeral.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.gcp) {
    const g = m.spec.provider.gcp;
    i.provider = "gcp";

    const parsedLink = parseLink(g.saKeyLink);

    i.gcp = {
      projectId: g.projectId,
      region: g.region,
      labels: tagsToInput(g.labels),
      tags: (g.tags || []).map((v) => ({ id: uuidv4(), value: v })),
      metadata: tagsToInput(g.metadata),
      network: g.network,
      saKeyLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      image: {
        type: g.image.exact ? "exact" : g.image.recommended ? "recommended" : "family",
        exact: g.image.exact || "",
        recommended: g.image.recommended || "",
        family: {
          project: g.image.family?.project || "",
          family: g.image.family?.family || "",
        },
      },
      nodePools: [],
      networking: networkingToInput(g.networking),
      preInstallScript: g.preInstallScript || "",
      autoscaler: autoscalerToInput(g.autoscaler),
    };

    const nodePools = (g.nodePools || []).map((np) => gcpNodePoolToInput(np));
    i.gcp.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.gcp.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.generic) {
    const g = m.spec.provider.generic;
    i.provider = "generic";

    i.generic = {
      location: g.location,
      networking: networkingToInput(g.networking as any),
      nodePools: [],
    };

    const nodePools = (g.nodePools || []).map((np) => unmanagedNodePoolToInput(np));
    i.generic.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.generic.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.hetzner) {
    const h = m.spec.provider.hetzner;
    i.provider = "hetzner";

    const parsedLink = parseLink(h.tokenSecretLink);

    i.hetzner = {
      region: h.region,
      networking: networkingToInput(h.networking as any),
      autoscaler: autoscalerToInput(h.autoscaler),
      firewallId: h.firewallId || "",
      floatingIPSelector: tagsToInput(h.floatingIPSelector),
      hetznerLabels: tagsToInput(h.hetznerLabels),
      image: h.image || "",
      networkId: h.networkId || "",
      preInstallScript: h.preInstallScript || "",
      sshKey: h.sshKey || "",
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      nodePools: [],
      dedicatedServerNodePools: [],
    };

    const nodePools = (h.nodePools || []).map((np) => hetznerNodePoolToInput(np));
    i.hetzner.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.hetzner.activeKeyNodePool = nodePools[0].id;
    }

    const unmanagedNodePools = (h.dedicatedServerNodePools || []).map((np) => unmanagedNodePoolToInput(np));
    i.hetzner.dedicatedServerNodePools = unmanagedNodePools;
    if (unmanagedNodePools.length > 0) {
      i.meta.hetzner.activeKeyDedicatedServerNodePool = unmanagedNodePools[0].id;
    }
  }
  if (m.spec.provider.lambdalabs) {
    const l = m.spec.provider.lambdalabs;
    i.provider = "lambdalabs";

    const parsedLink = parseLink(l.tokenSecretLink);

    i.lambdalabs = {
      region: l.region,
      autoscaler: autoscalerToInput(l.autoscaler),
      fileSystems: (l.fileSystems || []).map((fs) => ({ id: uuidv4(), value: fs })),
      preInstallScript: l.preInstallScript || "",
      sshKey: l.sshKey || "",
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      nodePools: [],
      unmanagedNodePools: [],
    };

    const nodePools = (l.nodePools || []).map((np) => lambdalabsNodePoolToInput(np));
    i.lambdalabs.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.lambdalabs.activeKeyNodePool = nodePools[0].id;
    }

    const unmanagedNodePools = (l.unmanagedNodePools || []).map((np) => unmanagedNodePoolToInput(np));
    i.lambdalabs.unmanagedNodePools = unmanagedNodePools;
    if (unmanagedNodePools.length > 0) {
      i.meta.lambdalabs.activeKeyUnmanagedNodePool = unmanagedNodePools[0].id;
    }
  }
  if (m.spec.provider.linode) {
    const l = m.spec.provider.linode;
    i.provider = "linode";

    const parsedLink = parseLink(l.tokenSecretLink);

    i.linode = {
      region: l.region,
      autoscaler: autoscalerToInput(l.autoscaler),
      preInstallScript: l.preInstallScript || "",
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      nodePools: [],
      authorizedKeys: (l.authorizedKeys || []).map((fs) => ({ id: uuidv4(), value: fs })),
      authorizedUsers: (l.authorizedUsers || []).map((fs) => ({ id: uuidv4(), value: fs })),
      firewallId: l.firewallId || "",
      image: l.image || "",
      networking: networkingToInput(l.networking),
      vpcId: l.vpcId,
    };

    const nodePools = (l.nodePools || []).map((np) => linodeNodePoolToInput(np));
    i.linode.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.linode.activeKeyNodePool = nodePools[0].id;
    }
  }
  if (m.spec.provider.oblivus) {
    const l = m.spec.provider.oblivus;
    i.provider = "oblivus";

    const parsedLink = parseLink(l.tokenSecretLink);

    i.oblivus = {
      datacenter: l.datacenter,
      autoscaler: autoscalerToInput(l.autoscaler),
      preInstallScript: l.preInstallScript || "",
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      nodePools: [],
      unmanagedNodePools: [],
      sshKeys: (l.sshKeys || []).map((fs) => ({ id: uuidv4(), value: fs })),
    };

    const nodePools = (l.nodePools || []).map((np) => oblivusNodePoolToInput(np));
    i.oblivus.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.oblivus.activeKeyNodePool = nodePools[0].id;
    }

    const unmanagedNodePools = (l.unmanagedNodePools || []).map((np) => unmanagedNodePoolToInput(np));
    i.oblivus.unmanagedNodePools = unmanagedNodePools;
    if (unmanagedNodePools.length > 0) {
      i.meta.oblivus.activeKeyUnmanagedNodePool = unmanagedNodePools[0].id;
    }
  }
  if (m.spec.provider.paperspace) {
    const p = m.spec.provider.paperspace;
    i.provider = "paperspace";

    const parsedLink = parseLink(p.tokenSecretLink);

    i.paperspace = {
      networkId: p.networkId,
      region: p.region,
      autoscaler: autoscalerToInput(p.autoscaler),
      preInstallScript: p.preInstallScript || "",
      tokenSecretLink: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      nodePools: [],
      unmanagedNodePools: [],
      sharedDrives: (p.sharedDrives || []).map((fs) => ({ id: uuidv4(), value: fs })),
      userIds: (p.userIds || []).map((fs) => ({ id: uuidv4(), value: fs })),
    };

    const nodePools = (p.nodePools || []).map((np) => paperspaceNodePoolToInput(np));
    i.paperspace.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.paperspace.activeKeyNodePool = nodePools[0].id;
    }

    const unmanagedNodePools = (p.unmanagedNodePools || []).map((np) => unmanagedNodePoolToInput(np));
    i.paperspace.unmanagedNodePools = unmanagedNodePools;
    if (unmanagedNodePools.length > 0) {
      i.meta.paperspace.activeKeyUnmanagedNodePool = unmanagedNodePools[0].id;
    }
  }
  if (m.spec.provider.triton) {
    const t = m.spec.provider.triton;
    i.provider = "triton";

    // Parse connection privateKeySecretLink
    const connectionParsedLink = parseLink(t.connection.privateKeySecretLink);

    // Determine loadBalancer type
    let loadBalancerType: "none" | "gateway" | "manual" = "gateway";
    if (t.loadBalancer?.none) {
      loadBalancerType = "none";
    } else if (t.loadBalancer?.manual) {
      loadBalancerType = "manual";
    }

    i.triton = {
      firewallEnabled: t.firewallEnabled || false,
      imageId: t.imageId,
      privateNetworkId: t.privateNetworkId,
      connection: {
        url: t.connection.url,
        account: t.connection.account,
        user: t.connection.user || "",
        privateKeySecretLink: {
          value: connectionParsedLink.name,
          link: connectionParsedLink.absolute,
        },
      },
      loadBalancer: {
        type: loadBalancerType,
        manual: {
          packageId: t.loadBalancer?.manual?.packageId || "",
          imageId: t.loadBalancer?.manual?.imageId || "",
          publicNetworkId: t.loadBalancer?.manual?.publicNetworkId || "",
          privateNetworkIds: (t.loadBalancer?.manual?.privateNetworkIds || []).map((pni) => ({
            id: uuidv4(),
            value: pni,
          })),
          metadata: tagsToInput(t.loadBalancer?.manual?.metadata),
          tags: tagsToInput(t.loadBalancer?.manual?.tags),
          logging: {
            nodePort: t.loadBalancer?.manual?.logging?.nodePort?.toString() || "",
            externalSyslog: t.loadBalancer?.manual?.logging?.externalSyslog || "",
          },
          count: t.loadBalancer?.manual?.count?.toString() || "",
          cnsInternalDomain: t.loadBalancer?.manual?.cnsInternalDomain || "",
          cnsPublicDomain: t.loadBalancer?.manual?.cnsPublicDomain || "",
        },
      },
      location: t.location,
      networking: networkingToInput(t.networking),
      autoscaler: autoscalerToInput(t.autoscaler),
      preInstallScript: t.preInstallScript || "",
      nodePools: [],
      sshKeys: (t.sshKeys || []).map((fs) => ({ id: uuidv4(), value: fs })),
    };

    const nodePools = (t.nodePools || []).map((np) => tritonNodePoolToInput(np));
    i.triton.nodePools = nodePools;
    if (nodePools.length > 0) {
      i.meta.triton.activeKeyNodePool = nodePools[0].id;
    }
  }

  // addons
  if (m.spec.addOns?.awsECR) {
    i.addons.awsECR = {
      isEnabled: true,
      roleArn: m.spec.addOns.awsECR.roleArn || "",
    };
  }
  if (m.spec.addOns?.awsEFS) {
    i.addons.awsEFS = {
      isEnabled: true,
      roleArn: m.spec.addOns.awsEFS.roleArn || "",
    };
  }
  if (m.spec.addOns?.awsELB) {
    i.addons.awsELB = {
      isEnabled: true,
      roleArn: m.spec.addOns.awsELB.roleArn || "",
    };
  }
  if (m.spec.addOns?.awsWorkloadIdentity) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.awsWorkloadIdentity);
    } catch (e) {
      value = "";
    }
    i.addons.awsWorkloadIdentity = {
      isEnabled: true,
      value: value,
    };
  }
  if (m.spec.addOns?.azureACR) {
    i.addons.azureACR = {
      isEnabled: true,
      clientId: m.spec.addOns.azureACR.clientId || "",
    };
  }
  if (m.spec.addOns?.azureWorkloadIdentity) {
    i.addons.azureWorkloadIdentity = {
      isEnabled: true,
      tenantId: m.spec.addOns.azureWorkloadIdentity.tenantId || "",
    };
  }
  if (m.spec.addOns?.byok) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.byok.config);
    } catch (e) {
      value = "";
    }

    const parsedLink = parseLink(m.spec.addOns.byok.location, { useInputCtx: true });
    i.addons.byok = {
      isEnabled: true,
      location: {
        value: parsedLink.name,
        link: parsedLink.absolute,
      },
      ignoreUpdates: m.spec.addOns.byok.ignoreUpdates,
      config: value,
    };
  }
  if (m.spec.addOns?.dashboard) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.dashboard);
    } catch (e) {
      value = "";
    }
    i.addons.dashboard = {
      isEnabled: true,
      value: value,
    };
  }
  if (m.spec.addOns?.headlamp) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.headlamp);
    } catch (e) {
      value = "";
    }
    i.addons.headlamp = {
      isEnabled: true,
      value: value,
    };
  }
  if (m.spec.addOns?.localPathStorage) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.localPathStorage);
    } catch (e) {
      value = "";
    }
    i.addons.localPathStorage = {
      isEnabled: true,
      value: value,
    };
  }
  if (m.spec.addOns?.logs) {
    i.addons.logs = {
      ...m.spec.addOns.logs,
      isEnabled: true,
    } as any;
  }
  if (m.spec.addOns?.metrics) {
    const scrapeAnnotated = m.spec.addOns.metrics.scrapeAnnotated;
    i.addons.metrics = {
      ...m.spec.addOns.metrics,
      isEnabled: true,
      scrapeAnnotated: {
        ...scrapeAnnotated,
        isEnabled: !!scrapeAnnotated,
        intervalSeconds: scrapeAnnotated?.intervalSeconds !== undefined ? String(scrapeAnnotated.intervalSeconds) : "",
      },
    } as any;
  }
  if (m.spec.addOns?.nvidia) {
    i.addons.nvidia = {
      isEnabled: true,
      taintGPUNodes: m.spec.addOns?.nvidia.taintGPUNodes || false,
    };
  }
  if (m.spec.addOns?.sysbox) {
    let value = "";
    try {
      value = jsYaml.dump(m.spec.addOns.sysbox);
    } catch (e) {
      value = "";
    }
    i.addons.sysbox = {
      isEnabled: true,
      value: value,
    };
  }
  if (m.spec.addOns?.registryMirror) {
    i.addons.registryMirror = {
      isEnabled: true,
      mirrors: [],
    };
    if (m.spec.addOns?.registryMirror.mirrors.length > 0) {
      const item = m.spec.addOns.registryMirror.mirrors[0];
      i.addons.registryMirror.mirrors.push({ id: uuidv4(), registry: item.registry, mirror: item.mirrors[0] || "" });
    }
  }

  i.firewall = (m.spec.firewall || []).map((f) => ({
    id: uuidv4(),
    sourceCIDR: f.sourceCIDR,
    description: f.description || "",
  }));

  return i;
}
